export function mapSnapshotResponseToUsersLoadsStats(data) {
  if (!data) return null;
  const totals = data.totals || {};
  const breakdowns = data.breakdowns || {};
  const loadsByStatus = breakdowns.loadsByStatus || {};
  const points = Array.isArray(data.points) ? data.points : [];

  const byMonthMap = {};
  points.forEach((p) => {
    const key = p.periodKey ? String(p.periodKey).slice(0, 7) : (p.periodKey || '').slice(0, 10);
    if (!key) return;
    const monthKey = key.length >= 7 ? key.slice(0, 7) : key;
    if (!byMonthMap[monthKey]) {
      byMonthMap[monthKey] = { month: monthKey, monthLabel: formatMonthLabel(monthKey), total: 0, delivered: 0, taken: 0 };
    }
    const t = p.totals || {};
    const b = p.breakdowns || {};
    const byStatus = b.loadsByStatus || {};
    byMonthMap[monthKey].total += t.loadsCreated ?? 0;
    byMonthMap[monthKey].delivered += byStatus.delivered ?? 0;
    byMonthMap[monthKey].taken += (byStatus.dispatched ?? 0) + (byStatus.pickedUp ?? 0) + (byStatus.delivered ?? 0) + (byStatus.onHold ?? 0);
  });
  const byMonth = Object.keys(byMonthMap)
    .sort()
    .map((k) => byMonthMap[k]);

  return {
    loadsCreated: totals.loadsCreated ?? 0,
    total: totals.loadsCreated ?? 0,
    byStatus: {
      Listed: loadsByStatus.listed ?? 0,
      Dispatched: loadsByStatus.dispatched ?? 0,
      'Picked Up': loadsByStatus.pickedUp ?? 0,
      Delivered: loadsByStatus.delivered ?? 0,
      'On Hold': loadsByStatus.onHold ?? 0,
      Cancelled: loadsByStatus.cancelled ?? 0
    },
    byMonth,
    totalProfit: totals.totalProfit ?? 0,
    chartPoints: points.map((p) => {
      const t = p.totals || {};
      const b = p.breakdowns || {};
      const byStatus = b.loadsByStatus || {};
      return {
        periodKey: p.periodKey,
        loadsCreated: t.loadsCreated ?? 0,
        delivered: byStatus.delivered ?? 0
      };
    })
  };
}

export function mapSnapshotResponseToLoadsStats(data) {
  if (!data) return null;
  const totals = data.totals || {};
  const breakdowns = data.breakdowns || {};
  const loadsByStatus = breakdowns.loadsByStatus || {};
  const points = Array.isArray(data.points) ? data.points : [];

  const byMonthMap = {};
  points.forEach((p) => {
    const key = p.periodKey ? String(p.periodKey).slice(0, 7) : null;
    if (!key || key.length < 7) return;
    if (!byMonthMap[key]) {
      byMonthMap[key] = { month: key, monthLabel: formatMonthLabel(key), total: 0, delivered: 0, taken: 0 };
    }
    const t = p.totals || {};
    const b = p.breakdowns || {};
    const byStatus = b.loadsByStatus || {};
    byMonthMap[key].total += t.totalLoads ?? 0;
    byMonthMap[key].delivered += byStatus.delivered ?? 0;
    const taken = (byStatus.dispatched ?? 0) + (byStatus.pickedUp ?? 0) + (byStatus.delivered ?? 0) + (byStatus.onHold ?? 0);
    byMonthMap[key].taken += taken;
  });
  const byMonth = Object.keys(byMonthMap)
    .sort()
    .map((k) => byMonthMap[k]);

  return {
    total: totals.totalLoads ?? 0,
    byStatus: {
      Listed: loadsByStatus.listed ?? 0,
      Dispatched: loadsByStatus.dispatched ?? 0,
      'Picked Up': loadsByStatus.pickedUp ?? 0,
      Delivered: loadsByStatus.delivered ?? 0,
      'On Hold': loadsByStatus.onHold ?? 0,
      Cancelled: loadsByStatus.cancelled ?? 0
    },
    byMonth
  };
}

export function mapSnapshotResponseToDashboard(data) {
  if (!data) return null;
  const totals = data.totals || {};
  const breakdowns = data.breakdowns || {};
  const loadsByStatus = breakdowns.loadsByStatus || {};

  return {
    overview: {
      totalLoads: totals.totalLoads ?? 0,
      totalRevenue: totals.totalRevenue ?? 0,
      totalProfit: totals.totalProfit ?? 0,
      delivered: loadsByStatus.delivered ?? 0
    },
    loads: {
      total: totals.totalLoads ?? 0,
      byStatus: {
        listed: loadsByStatus.listed ?? 0,
        dispatched: loadsByStatus.dispatched ?? 0,
        pickedUp: loadsByStatus.pickedUp ?? 0,
        delivered: loadsByStatus.delivered ?? 0,
        onHold: loadsByStatus.onHold ?? 0,
        cancelled: loadsByStatus.cancelled ?? 0
      }
    }
  };
}

function formatMonthLabel(monthKey) {
  if (!monthKey || monthKey.length < 7) return monthKey || '';
  const [y, m] = monthKey.split('-');
  const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * Maps snapshot response to shape expected by DashboardStats mappers
 * (mapKPIData, mapLoadsByStatusDonut, mapMoneyTrendLine)
 */
export function mapSnapshotToLegacyDashboard(data) {
  if (!data) return null;
  const totals = data.totals || {};
  const breakdowns = data.breakdowns || {};
  const loadsByStatus = breakdowns.loadsByStatus || {};
  const points = Array.isArray(data.points) ? data.points : [];

  const overview = {
    totalLoads: totals.totalLoads ?? 0,
    revenue: totals.totalRevenue ?? 0,
    profit: totals.totalProfit ?? 0,
    delivered: loadsByStatus.delivered ?? 0
  };

  const loads = {
    total: totals.totalLoads ?? 0,
    byStatus: {
      listed: loadsByStatus.listed ?? 0,
      dispatched: loadsByStatus.dispatched ?? 0,
      pickedUp: loadsByStatus.pickedUp ?? 0,
      delivered: loadsByStatus.delivered ?? 0,
      onHold: loadsByStatus.onHold ?? 0,
      cancelled: loadsByStatus.cancelled ?? 0
    }
  };

  const categories = points.map((p) => p.periodKey || '');
  const series = {
    moneyTrend: {
      revenue: points.map((p) => (p.totals && p.totals.totalRevenue) ?? 0),
      expense: points.map((p) => (p.totals && p.totals.totalExpense) ?? 0),
      profit: points.map((p) => (p.totals && p.totals.totalProfit) ?? 0)
    },
    categories
  };

  return {
    overview,
    loads,
    series,
    deltas: { totalLoads: 0, revenue: 0, profit: 0 }
  };
}
